import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, homeFor } from '../context/AuthContext.jsx';
import PublicShell from '../components/PublicShell.jsx';
import CubMascot from '../components/CubMascot.jsx';

/**
 * CyberPath - the long view of the journey, Class 4 to Class 12.
 *
 * Four tracks, one leading into the next, drawn as a connected block
 * diagram so the progression reads at a glance: a child starts as a
 * Cyber Smart Kid and, four stages later, is a Cyber Innovator building
 * and documenting a defensive security tool.
 *
 * Everything here is the plan, stated plainly. No track is claimed to be
 * finished or on sale; this is the roadmap, not the shop.
 */

const TRACKS = [
  {
    n: 1,
    classes: 'Classes 4–5',
    name: 'Cyber Smart Kids',
    level: 'Beginner',
    color: 'mint',
    icon: '🌱',
    topics: [
      'Personal information, passwords & privacy',
      'Cyberbullying',
      'Suspicious messages & scams',
      'Safe gaming',
      'Responsible AI use',
      'Telling a trusted adult',
    ],
    project: 'Create a digital-safety campaign for the school.',
  },
  {
    n: 2,
    classes: 'Classes 6–7',
    name: 'Cyber Explorers',
    level: 'Foundation',
    color: 'sky',
    icon: '🧭',
    topics: [
      'How websites & the internet work',
      'Basic encryption & password security',
      'Phishing detection',
      'Account protection',
      'Scratch & beginner Python',
      'Computational thinking',
    ],
    project: 'Build a phishing-awareness quiz, or a password-strength demo.',
  },
  {
    n: 3,
    classes: 'Classes 8–10',
    name: 'Ethical Hacking',
    level: 'Intermediate',
    color: 'grape',
    icon: '🛠️',
    topics: [
      'Linux & networking fundamentals',
      'Python scripting',
      'Secure coding',
      'Web security & preventing vulnerabilities',
      'Security logs',
      'Incident response & threat modelling',
    ],
    project: 'Build a defensive log analyser, or a secure sample web app.',
  },
  {
    n: 4,
    classes: 'Classes 11–12',
    name: 'Cyber Innovators',
    level: 'Advanced',
    color: 'coral',
    icon: '🚀',
    topics: [
      'Network & cloud security',
      'Secure software development & authentication',
      'Digital forensics & incident response',
      'Vulnerability assessment in authorised labs',
      'AI security & threat detection',
      'Responsible disclosure',
    ],
    project: 'Develop and document a defensive security tool, with test results.',
  },
];

export default function CyberPath() {
  const { user } = useAuth();

  useEffect(() => {
    document.title = 'CyberPath · CyberCub';
  }, []);

  return (
    <PublicShell>
      {/* ---------------- opening ---------------- */}
      <section className="about-head">
        <div className="about-head-text">
          <span className="home-eyebrow">CyberPath</span>
          <h1>
            One path, Class 4
            <br />
            <span className="hl">to Class 12.</span>
          </h1>
          <p className="home-lede">
            Cyber safety is not one lesson — it grows with the child. CyberPath is the whole journey
            in four stages, each building on the one before: from staying safe online to building
            and documenting a real defensive security tool.
          </p>
        </div>
        <CubMascot avatar="cub-teal" mood="star" size={150} className="anim-float" />
      </section>

      {/* ---------------- the block diagram ---------------- */}
      <section className="home-section">
        <ol className="cp-flow" aria-label="The four stages of CyberPath">
          {TRACKS.map((t, i) => (
            <li key={t.n} className={`cp-stage t-${t.color}`}>
              <article className="cp-block">
                <header className="cp-block-head">
                  <span className="cp-stage-n" aria-hidden="true">{t.n}</span>
                  <div>
                    <span className="cp-classes">{t.classes}</span>
                    <h2 className="cp-name">
                      <span className="cp-icon" aria-hidden="true">{t.icon}</span>
                      {t.name}
                    </h2>
                  </div>
                  <span className="cp-level">{t.level}</span>
                </header>

                <div className="cp-body">
                  <span className="cp-label">What you learn</span>
                  <ul className="cp-topics">
                    {t.topics.map((topic) => <li key={topic}>{topic}</li>)}
                  </ul>
                </div>

                <footer className="cp-project">
                  <span className="cp-project-label">🏆 Final project</span>
                  <p>{t.project}</p>
                </footer>
              </article>

              {i < TRACKS.length - 1 && (
                <span className="cp-arrow" aria-hidden="true">→</span>
              )}
            </li>
          ))}
        </ol>

        <p className="cp-note">
          Each stage leads into the next, so a child can join at their class level and keep climbing.
          Tracks open over time — this is the roadmap, not everything is on sale yet.
        </p>
      </section>

      {/* ---------------- close ---------------- */}
      <section className="home-section home-band about-close">
        <h2 className="home-h2">Start where you are</h2>
        <p className="home-sub">
          The two courses live today sit on this path — Cyber Smart Kids for the early stages,
          Ethical Hacker as it climbs. Have a look at what is ready now.
        </p>
        <div className="home-cta">
          {user ? (
            <Link to={homeFor(user.role)} className="btn btn-lg">My courses →</Link>
          ) : (
            <>
              <Link to="/login" className="btn btn-lg">Log in</Link>
              <Link to="/#courses" className="btn btn-lg btn-ghost">See the courses</Link>
            </>
          )}
        </div>
      </section>
    </PublicShell>
  );
}
